import React, { useState } from 'react';
import { useAppConfig } from '../../context/AppConfigContext';
import { useForm } from '../../context/FormContext';
import PetsSummaryBar from './PetsSummaryBar';
import PetDrawer from './PetDrawer';

interface Phase2HeaderProps {
  /** Posición en la secuencia "Diseña tu menú" (1-5):
   *  1 = recetas, 2 = tipo de dieta, 3 = como empezar,
   *  4 = Step12_Extras, 5 = Step13_Checkout. */
  step: 1 | 2 | 3 | 4 | 5;
}

export const Phase2Header: React.FC<Phase2HeaderProps> = ({ step }) => {
  const { t, logoImage } = useAppConfig();
  const {
    setCurrentStep,
    petsCompleted,
    formData,
    updateFormData,
    updatePetSnapshotData,
    removePetSnapshot,
    replaceCurrentWithPetSnapshot,
    restorePetForEditing,
    currentStep,
  } = useForm();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [firstPetId, setFirstPetId] = useState<string | undefined>(undefined);

  const handlePillClick = (petId: string) => {
    setFirstPetId(petId);
    setIsDrawerOpen(true);
  };

  const handleRemoveCurrentPet = () => {
    const fallbackPet = petsCompleted[petsCompleted.length - 1];
    if (fallbackPet) replaceCurrentWithPetSnapshot(fallbackPet.id, currentStep);
  };

  const handleEditCompletedPet = (petId: string, targetStep: number) => {
    restorePetForEditing(petId, targetStep);
    setIsDrawerOpen(false);
  };

  // La barra tiene 4 pestañas visibles: RECETAS · TU PLAN · EXTRAS · CHECKOUT.
  // "¿Cómo quieres empezar?" (step 3) NO tiene pestaña propia: comparte TU PLAN.
  // Por eso las 5 pantallas se mapean a 4 posiciones (step 2 y 3 → posición 2).
  const TAB_POSITION = [0, 1, 2, 2, 3, 4];
  const totalSteps = 4;
  const current = TAB_POSITION[step];
  const percent = Math.round((current / totalSteps) * 100);

  return (
    <>
      <header className="bg-white px-4 sm:px-6 py-1 flex items-center justify-between flex-shrink-0">
        <img
          src={logoImage}
          alt="Milo y Lola"
          className="h-8 sm:h-10 object-contain cursor-pointer"
          onClick={() => setCurrentStep(1)}
        />
        <div className="flex items-center gap-3 sm:gap-4">
          <span className="font-fredoka text-black/50 text-xs whitespace-nowrap">
            {t('funnel.phase_label_2', 'Diseña tu menú')} · {current}/{totalSteps}
          </span>
          <span className="font-fredoka text-[#008BBC] text-sm font-medium whitespace-nowrap">
            {t('funnel.progress', '{progress}% completado', { progress: percent })}
          </span>
        </div>
      </header>

      <div className="h-1.5 bg-[#E9E3D7]/60 flex-shrink-0">
        <div className="h-full bg-[#008BBC] transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>

      <PetsSummaryBar onPillClick={handlePillClick} />

      <PetDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        petsCompleted={petsCompleted}
        formData={formData}
        onUpdateCurrentPet={updateFormData}
        onUpdateCompletedPet={updatePetSnapshotData}
        onEditCompletedField={handleEditCompletedPet}
        onRemoveCurrentPet={handleRemoveCurrentPet}
        onRemoveCompletedPet={removePetSnapshot}
        firstPetId={firstPetId}
      />
    </>
  );
};
